import { PspContext } from "@/config/contexts";
import { useContext, useEffect, useState } from "react";
import ChangePasswordForm from "./changePasswordForm";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { useNavigate } from "react-router-dom";
import PspButton from "@/components/pspButton/PspButton";
import { translate } from "@/config/translator";
import { toast } from "react-toastify";
import {
  changePassword,
  checkPassword,
  getPasswordPolicy,
  PasswordPolicy,
} from "@/api/openapi";
import { setAccessTokenStorage, setRefreshTokenStorage } from "@/config/utils";
import { PasswordPolicyError } from "@/config/types";
import { FullScreenSpinner } from "@/components/fullScreenSpinner/FullScreenSpinner";

export default function ChangePasswordScreen() {
  const { lang } = useContext(PspContext);
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [wrongPassword, setWrongPassword] = useState(false);
  const [changePasswordButtonDisabled, setChangePasswordButtonDisabled] =
    useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState<
    Record<PasswordPolicyError, boolean>
  >({
    missing_digit: true,
    missing_uppercase_letter: true,
    missing_lowercase_letter: true,
    too_short: true,
    missing_symbol: true,
  });

  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy>();
  useEffect(() => {
    getPasswordPolicy().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setPasswordPolicy(data);
      }
    });
  }, [lang]);

  const passwordsDontMatch = newPassword !== confirmNewPassword;
  const emptyPassword = newPassword.length === 0 || oldPassword.length === 0;
  const passwordInvalid =
    passwordRequirements.missing_digit ||
    passwordRequirements.missing_uppercase_letter ||
    passwordRequirements.missing_symbol ||
    passwordRequirements.missing_lowercase_letter ||
    passwordRequirements.too_short;

  async function onChangePassword(
    oldPassword: string,
    newPassword: string,
  ): Promise<void> {
    if (!passwordPolicy) throw new Error();
    setChangePasswordButtonDisabled(true);

    try {
      // Check old password
      const { error: checkError, status } = await checkPassword({
        body: { password: oldPassword },
      });
      if (checkError) {
        if (status === 403) setWrongPassword(true);
        else toast(translate("somethingWentWrong", lang));
        throw new Error();
      }

      // make sure that the password satisfies all requirements before
      // making an API call to change it
      for (const valid of Object.values(passwordRequirements)) {
        if (valid) throw new Error("unsatisfied password requirement");
      }

      // Change password
      const { error: changeError, data: tokenData } = await changePassword({
        body: { newPassword, oldPassword },
      });
      if (changeError) {
        toast(translate("somethingWentWrong", lang));
        throw new Error();
      }

      setAccessTokenStorage(tokenData.accessToken);
      setRefreshTokenStorage(tokenData.refreshToken);

      toast(translate("passwordChangedSuccessfully", lang));
      setWrongPassword(false);
      navigate(-1);
    } finally {
      setChangePasswordButtonDisabled(false);
    }
  }

  return (
    <>
      <TopBarMobile header="changePassword" onBack={() => navigate(-1)} />
      <div className="p-6 flex flex-col justify-between h-full">
        {passwordPolicy ? (
          <>
            <ChangePasswordForm
              passwordPolicy={passwordPolicy}
              setPasswordRequirements={setPasswordRequirements}
              passwordRequirements={passwordRequirements}
              oldPassword={oldPassword}
              newPassword={newPassword}
              confirmNewPassword={confirmNewPassword}
              passwordsDontMatch={passwordsDontMatch}
              setOldPassword={setOldPassword}
              setNewPassword={setNewPassword}
              setConfirmNewPassword={setConfirmNewPassword}
              setWrongPassword={setWrongPassword}
              wrongPassword={wrongPassword}
            />
            <PspButton
              onClick={() => onChangePassword(oldPassword, newPassword)}
              appearance="primary"
              disabled={
                passwordsDontMatch ||
                emptyPassword ||
                changePasswordButtonDisabled ||
                passwordInvalid
              }
            >
              {translate("save", lang)}
            </PspButton>
          </>
        ) : (
          <FullScreenSpinner />
        )}
      </div>
    </>
  );
}
